import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface PolicySuggestion {
    content: string;
    type: 'template' | 'improvement' | 'completion';
    confidence: number;
}

export interface PolicyPromptOptions {
    type:
        | 'hr'
        | 'it'
        | 'safety'
        | 'finance'
        | 'general'
        | 'improve'
        | 'complete'
        | 'summarize'
        | 'concise'
        | 'expand'
        | 'polish'
        | 'grammar'
        | 'refine_selection';
    context?: string;
    existingPolicy?: string;
    companyName?: string;
    tone?: 'formal' | 'casual' | 'professional' | 'friendly' | 'authoritative' | 'empathetic' | 'simple';
    // For selection-based refinement
    selectedText?: string;
    fullDocument?: string;
}

interface GeminiResponse {
    candidates: {
        content: {
            parts: {
                text: string;
            }[];
        };
    }[];
}

@Injectable({
    providedIn: 'root',
})
export class PolicyAiService {
    private apiKey: string | null = null;
    // The `-latest` alias: pinned ids die when Google retires them
    // (gemini-2.0-flash 404s on generateContent since 2026-08 while still
    // showing in ListModels — the triage nlFind function hit exactly this).
    private readonly apiUrl =
        'https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent';
    private readonly API_KEY_STORAGE_KEY = 'newgenus_gemini_api_key';

    constructor(private http: HttpClient) {
        // Load API key from localStorage on service initialization
        this.loadApiKeyFromStorage();
    }

    /**
     * Set the API key for Gemini API and save to localStorage
     */
    setApiKey(apiKey: string): void {
        this.apiKey = apiKey;
        // Save to localStorage for persistence across sessions
        try {
            localStorage.setItem(this.API_KEY_STORAGE_KEY, apiKey);
        } catch (error) {
            console.warn('Failed to save API key to localStorage:', error);
        }
    }

    /**
     * Check if API key is available
     */
    hasApiKey(): boolean {
        return !!this.apiKey;
    }

    /**
     * Clear the API key from memory and localStorage
     */
    clearApiKey(): void {
        this.apiKey = null;
        try {
            localStorage.removeItem(this.API_KEY_STORAGE_KEY);
        } catch (error) {
            console.warn('Failed to remove API key from localStorage:', error);
        }
    }

    /**
     * Load API key from localStorage
     */
    private loadApiKeyFromStorage(): void {
        try {
            const storedApiKey = localStorage.getItem(this.API_KEY_STORAGE_KEY);
            if (storedApiKey) {
                this.apiKey = storedApiKey;
            }
        } catch (error) {
            console.warn('Failed to load API key from localStorage:', error);
        }
    }

    /**
     * Generate policy content using Google Gemini API
     */
    generatePolicyContent(options: PolicyPromptOptions): Observable<PolicySuggestion> {
        if (!this.apiKey) {
            return throwError(() => new Error('Gemini API key not configured'));
        }

        const prompt = this.buildPrompt(options);

        // DEBUG: Log the full prompt being sent to the AI
        console.log('=== AI POLICY ASSISTANT: PROMPT SENT TO GEMINI ===');
        console.log('Options:', JSON.stringify(options, null, 2));
        console.log('Full Prompt:', prompt);
        console.log('=================================================');

        const requestBody = {
            contents: [
                {
                    parts: [
                        {
                            text: prompt,
                        },
                    ],
                },
            ],
            generationConfig: {
                temperature: 0.7,
                topK: 40,
                topP: 0.95,
                // The flash alias resolves to a thinking model whose thoughts bill
                // against this cap — 1024 starves the visible reply.
                maxOutputTokens: 4096,
            },
        };

        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });

        return this.http.post<GeminiResponse>(`${this.apiUrl}?key=${this.apiKey}`, requestBody, { headers }).pipe(
            map((response: GeminiResponse) => {
                // Join ALL parts, not parts[0] — thinking models can fragment the
                // visible reply across several text parts.
                const content = (response?.candidates?.[0]?.content?.parts ?? []).map((p) => p?.text ?? '').join('');
                return {
                    content: content.trim(),
                    type:
                        options.type === 'improve'
                            ? 'improvement'
                            : options.type === 'complete'
                              ? 'completion'
                              : 'template',
                    confidence: this.calculateConfidence(content),
                } as PolicySuggestion;
            }),
            catchError((error) => {
                console.error('Gemini API Error:', error);
                let errorMessage = 'Failed to generate policy content. Please try again.';

                if (error.status === 404) {
                    errorMessage = 'API endpoint not found. Please check your API key and try again.';
                } else if (error.status === 401 || error.status === 403) {
                    errorMessage = 'Invalid API key or insufficient permissions. Please check your API key.';
                } else if (error.status === 429) {
                    errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
                } else if (error.error?.error?.message) {
                    errorMessage = `API Error: ${error.error.error.message}`;
                }

                return throwError(() => new Error(errorMessage));
            })
        );
    }

    /**
     * Get available policy templates
     */
    getAvailableTemplates(): { type: string; label: string; description: string }[] {
        return [
            {
                type: 'hr',
                label: 'Human Resources',
                description: 'Employee handbook, conduct policies, benefits',
            },
            {
                type: 'it',
                label: 'IT & Security',
                description: 'Data protection, cybersecurity, technology usage',
            },
            {
                type: 'safety',
                label: 'Health & Safety',
                description: 'Workplace safety, emergency procedures, health protocols',
            },
            {
                type: 'finance',
                label: 'Financial',
                description: 'Expense policies, procurement, financial controls',
            },
            {
                type: 'general',
                label: 'General Business',
                description: 'Operations, communication, general business policies',
            },
        ];
    }

    private buildPrompt(options: PolicyPromptOptions): string {
        const companyName = options.companyName || '[Company Name]';
        const tone = options.tone || 'professional';

        switch (options.type) {
            case 'improve':
                return `Please improve and refine the following organizational policy. Make it more clear, comprehensive, and professional while maintaining the original intent. Ensure it follows best practices for organizational policies:

Existing Policy:
${options.existingPolicy}

Please provide an improved version that is:
- Clear and easy to understand
- Comprehensive but concise
- Professional in tone
- Compliant with general best practices
- Well-structured with proper sections

Context: ${options.context || 'General organizational policy'}`;

            case 'complete':
                return `Please complete the following partial organizational policy. Continue from where it left off, maintaining the same tone and structure:

Partial Policy:
${options.existingPolicy}

Context: ${options.context || 'Complete this policy following organizational best practices'}

Please complete this policy ensuring it:
- Maintains consistency with the existing content
- Follows proper policy structure
- Is comprehensive and actionable
- Uses ${tone} tone`;

            case 'summarize':
                return `Please create a concise summary of the following organizational policy. Capture the key points, main requirements, and essential information:

Existing Policy:
${options.existingPolicy}

Context: ${options.context || 'Create an executive summary suitable for quick reference'}

Please provide a summary that:
- Captures all key requirements and obligations
- Is easy to read and understand quickly
- Maintains accuracy of the original policy
- Uses bullet points where appropriate
- Uses ${tone} tone`;

            case 'concise':
                return `Please make the following organizational policy more concise and streamlined. Remove redundancy, simplify language, and tighten the content while preserving all essential information:

Existing Policy:
${options.existingPolicy}

Context: ${options.context || 'Streamline this policy while keeping all critical information'}

Please provide a more concise version that:
- Removes unnecessary words and redundancy
- Uses clear, direct language
- Maintains all essential requirements and procedures
- Preserves the policy structure
- Uses ${tone} tone`;

            case 'expand':
                return `Please expand the following organizational policy with more detail, examples, and comprehensive coverage. Add helpful clarifications and additional guidance:

Existing Policy:
${options.existingPolicy}

Context: ${options.context || 'Add more detail and examples to make this policy more comprehensive'}

Please expand this policy by:
- Adding more detailed explanations of requirements
- Including examples where helpful
- Adding clarifying language for ambiguous areas
- Expanding on procedures and responsibilities
- Uses ${tone} tone`;

            case 'polish':
                return `Please polish and refine the following organizational policy. Improve the flow, word choice, and overall readability while maintaining the same meaning and structure:

Existing Policy:
${options.existingPolicy}

Context: ${options.context || 'Polish this policy to make it more refined and polished'}

Please polish this policy by:
- Improving sentence structure and flow
- Enhancing word choice for clarity and impact
- Smoothing transitions between sections
- Making the language more elegant and refined
- Maintaining the original meaning and intent
- Uses ${tone} tone`;

            case 'grammar':
                return `Please fix all grammar, spelling, punctuation, and typographical errors in the following organizational policy. Do not change the meaning or structure, only correct errors:

Existing Policy:
${options.existingPolicy}

Context: ${options.context || 'Fix all grammar and spelling errors'}

Please correct:
- Spelling mistakes
- Grammar errors
- Punctuation issues
- Capitalization errors
- Typographical errors
- Maintain the exact same meaning and structure
- Keep the ${tone} tone`;

            case 'refine_selection':
                return `You are refining a SPECIFIC SECTION of a larger organizational policy document. 

IMPORTANT: You must ONLY return the refined version of the selected text. Do NOT include any surrounding content or the full document.

FULL DOCUMENT (for context only - do not include in response):
${options.fullDocument}

SELECTED TEXT TO REFINE (refine ONLY this text):
${options.selectedText}

INSTRUCTIONS:
${options.context || 'Improve this section while maintaining consistency with the rest of the document'}

Please refine the selected text by:
- Improving clarity and readability
- Maintaining consistency with the document's overall tone and style
- Using ${tone} tone
- Keeping the same general meaning and intent
- Making it flow well with the surrounding content

CRITICAL: Return ONLY the refined version of the selected text. Do not include any explanations, the full document, or surrounding content.`;

            case 'hr':
                return `Create a comprehensive Human Resources policy for ${companyName}. The policy should cover:
- Employee conduct and behavior expectations
- Anti-discrimination and harassment policies
- Performance management guidelines
- Leave and time-off policies
- Professional development expectations

Context: ${options.context || 'Standard HR policy for professional organization'}
Tone: ${tone}

Please structure it with clear sections, specific guidelines, and actionable items.`;

            case 'it':
                return `Create a comprehensive IT and Security policy for ${companyName}. The policy should cover:
- Data protection and privacy
- Cybersecurity best practices
- Technology usage guidelines
- Password and access management
- Incident reporting procedures

Context: ${options.context || 'Standard IT security policy for business organization'}
Tone: ${tone}

Please structure it with clear sections, specific guidelines, and compliance requirements.`;

            case 'safety':
                return `Create a comprehensive Health and Safety policy for ${companyName}. The policy should cover:
- Workplace safety procedures
- Emergency response protocols
- Health and wellness guidelines
- Accident reporting procedures
- Safety training requirements

Context: ${options.context || 'Standard workplace safety policy'}
Tone: ${tone}

Please structure it with clear sections, specific procedures, and safety compliance requirements.`;

            case 'finance':
                return `Create a comprehensive Financial policy for ${companyName}. The policy should cover:
- Expense approval and reimbursement procedures
- Procurement and purchasing guidelines
- Financial controls and authorization limits
- Budgeting and financial planning processes
- Audit and compliance requirements

Context: ${options.context || 'Standard financial policy for business organization'}
Tone: ${tone}

Please structure it with clear sections, specific procedures, and financial controls.`;

            default:
                return `Create a comprehensive organizational policy for ${companyName} based on the following context:

Context: ${options.context || 'General business policy'}

The policy should be:
- Professional and clearly written
- Well-structured with proper sections
- Actionable and enforceable
- Compliant with general best practices
- Written in a ${tone} tone

Please include appropriate sections such as purpose, scope, responsibilities, procedures, and compliance requirements.`;
        }
    }

    private calculateConfidence(content: string): number {
        // Simple confidence calculation based on content length and structure
        if (!content || content.length < 100) return 0.3;
        if (content.length < 300) return 0.6;
        if (content.includes('Purpose:') || content.includes('Scope:') || content.includes('Policy:')) return 0.9;
        return 0.7;
    }
}
